import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#003D5C] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center text-blue-200 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Privacy Policy</h1>
          <p className="text-blue-200 mt-2">Last updated: December 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          <div className="bg-[#F0F7FF] p-6 rounded-xl mb-8 border-l-4 border-[#0077B6]">
            <p className="text-gray-700 m-0">
              At Utmost Financial Inc. ("we," "us," or "our"), we are committed to protecting your privacy and safeguarding your personal information. This Privacy Policy explains how we collect, use, disclose, and protect your information.
            </p>
          </div>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Database className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Information We Collect</h2>
            </div>
            <p className="text-gray-600">We may collect the following types of personal information:</p>
            <ul className="text-gray-600 space-y-2">
              <li><strong>Contact Information:</strong> Name, address, email, phone number</li>
              <li><strong>Financial Information:</strong> Income, assets, debts, credit history (for mortgage and insurance applications)</li>
              <li><strong>Identification:</strong> Date of birth, social insurance number (when required for applications)</li>
              <li><strong>Health Information:</strong> Medical history (for insurance applications only)</li>
              <li><strong>Employment Information:</strong> Employer details, occupation, employment history</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Eye className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">How We Use Your Information</h2>
            </div>
            <p className="text-gray-600">We use your personal information to:</p>
            <ul className="text-gray-600 space-y-2">
              <li>Process mortgage, insurance, and investment applications</li>
              <li>Provide financial advice and recommendations</li>
              <li>Communicate with you about your accounts and services</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Send you newsletters and marketing communications (with your consent)</li>
              <li>Improve our services and website experience</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Information Sharing</h2>
            </div>
            <p className="text-gray-600">We may share your information with:</p>
            <ul className="text-gray-600 space-y-2">
              <li><strong>Lenders and Insurers:</strong> To process your applications</li>
              <li><strong>Regulatory Bodies:</strong> As required by law (e.g., FSRA, provincial regulators)</li>
              <li><strong>Service Providers:</strong> Who assist us in operating our business</li>
              <li><strong>Credit Bureaus:</strong> For credit verification purposes</li>
            </ul>
            <p className="text-gray-600 mt-4">We do not sell your personal information to third parties.</p>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Lock className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Data Security</h2>
            </div>
            <p className="text-gray-600">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Secure encrypted connections (SSL/TLS) for all data transmission</li>
              <li>Restricted access to personal information on a need-to-know basis</li>
              <li>Regular security assessments and updates</li>
              <li>Secure storage of physical documents</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Your Rights</h2>
            </div>
            <p className="text-gray-600">You have the right to:</p>
            <ul className="text-gray-600 space-y-2">
              <li>Access your personal information we hold</li>
              <li>Request correction of inaccurate information</li>
              <li>Withdraw consent for marketing communications</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>File a complaint with the relevant privacy commissioner</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700 m-0"><strong>Utmost Financial Inc.</strong></p>
              <p className="text-gray-600 m-0">A-970 Sargent Avenue, Winnipeg, MB R3E0E3</p>
              <p className="text-gray-600 m-0">Email: privacy@utmostfinancial.ca</p>
              <p className="text-gray-600 m-0">Phone: 1-877-239-9060</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
